/**
 * MouthGestureEngine - Global component for mouth gesture control
 * Detects smile and mouth open gestures using MediaPipe tasks-vision
 * Dispatches scanning actions based on user's configured gesture-to-action mappings
 * 
 * Uses the unified scanning system for consistent behavior with external switches
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { getScoped, setScoped, removeScoped } from '@/lib/userScopedStorage';
import { useLocation } from 'react-router-dom';
import { useInputMethod } from '@/contexts/InputMethodContext';
import { InputMethod } from '@/lib/inputMethods';
import { getUserSettings } from '@/lib/userSettings';
import { dispatchScanningAction, ScanningAction } from '@/lib/scanningSystem';

// MediaPipe Face Landmarker landmark indices for mouth
const MOUTH_LANDMARKS = {
  LEFT_CORNER: 61,
  RIGHT_CORNER: 291,
  UPPER_LIP: 13,
  LOWER_LIP: 14
};

interface CalibrationData {
  neutralWidth: number;
  neutralHeight: number;
  smileThreshold: number;
  openThreshold: number;
  puckerThreshold?: number;
}

const DEFAULT_CALIBRATION: CalibrationData = {
  neutralWidth: 0.15,
  neutralHeight: 0.02,
  smileThreshold: 1.2,
  openThreshold: 2.0,
  puckerThreshold: 0.8
};

export function MouthGestureEngine() {
  const location = useLocation();
  const { inputMethod } = useInputMethod();
  
  // Skip if on setup page - setup page manages its own MediaPipe instance
  const isSetupPage = location.pathname.includes('/control/');
  const shouldTrack = inputMethod === InputMethod.MOUTH_GESTURES && !isSetupPage;
  
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const faceLandmarkerRef = useRef<any>(null);
  const animationFrameRef = useRef<number>();
  const lastActionRef = useRef<number>(0);
  const [isActive, setIsActive] = useState(false);
  const isActiveRef = useRef(false);
  
  // Listen for control method change to stop immediately
  useEffect(() => {
    const handleControlMethodChange = () => {
      const currentMethod = sessionStorage.getItem('controlMethod') || getScoped('ayuni_input_method');
      if (currentMethod !== 'mouth_gestures') {
        // Immediately stop tracking
        isActiveRef.current = false;
      }
    };
    
    window.addEventListener('controlMethodChanged', handleControlMethodChange);
    window.addEventListener('inputMethodChanged', handleControlMethodChange);
    
    return () => {
      window.removeEventListener('controlMethodChanged', handleControlMethodChange);
      window.removeEventListener('inputMethodChanged', handleControlMethodChange);
    };
  }, []);
  
  // Get user settings including gesture-action mappings
  const getGestureConfig = useCallback(() => {
    const settings = getUserSettings();
    // Calibration is stored separately in localStorage
    let calibration = DEFAULT_CALIBRATION;
    try {
      // Try both possible localStorage keys for calibration data
      const storedCal = getScoped('ayuni_mouth_calibration') || getScoped('mouthGestureCalibration');
      if (storedCal) {
        calibration = JSON.parse(storedCal);
      }
    } catch {}
    const gestureActions = settings.mouthGestureActions || {};
    return { calibration, gestureActions };
  }, []);
  
  // Cleanup function
  const cleanup = useCallback(() => {
    isActiveRef.current = false;
    
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = undefined;
    }
    
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => {
        track.stop();
      });
      videoRef.current.srcObject = null;
      videoRef.current.remove();
      videoRef.current = null;
    }
    
    if (faceLandmarkerRef.current) {
      try {
        faceLandmarkerRef.current.close();
      } catch (e) {}
      faceLandmarkerRef.current = null;
    }
    
    setIsActive(false);
  }, []);
  
  // Initialize MediaPipe using tasks-vision (same as HeadTrackingEngine)
  const initializeMediaPipe = useCallback(async () => {
    try {
      const { FaceLandmarker, FilesetResolver } = await import("@mediapipe/tasks-vision");
      const { MEDIAPIPE_WASM_PATH, getFaceLandmarkerOptions } = await import("@/lib/mediapipeConfig");
      
      const filesetResolver = await FilesetResolver.forVisionTasks(MEDIAPIPE_WASM_PATH);
      
      faceLandmarkerRef.current = await FaceLandmarker.createFromOptions(
        filesetResolver,
        getFaceLandmarkerOptions('GPU', false)
      );
      
      return true;
    } catch {
      try {
        const { FaceLandmarker, FilesetResolver } = await import("@mediapipe/tasks-vision");
        const { MEDIAPIPE_WASM_PATH, getFaceLandmarkerOptions } = await import("@/lib/mediapipeConfig");
        
        const filesetResolver = await FilesetResolver.forVisionTasks(MEDIAPIPE_WASM_PATH);
        
        faceLandmarkerRef.current = await FaceLandmarker.createFromOptions(
          filesetResolver,
          getFaceLandmarkerOptions('CPU', false)
        );
        
        return true;
      } catch {
        return false;
      }
    }
  }, []);
  
  // Setup webcam
  const setupWebcam = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: 640, height: 480 }
      });
      
      const video = document.createElement("video");
      video.srcObject = stream;
      video.style.display = "none";
      document.body.appendChild(video);
      await video.play();
      
      videoRef.current = video;
      return true;
    } catch {
      return false;
    }
  }, []);
  
  // Tracking loop
  const runTrackingLoop = useCallback(() => {
    if (!isActiveRef.current || !faceLandmarkerRef.current || !videoRef.current) {
      if (isActiveRef.current) {
        animationFrameRef.current = requestAnimationFrame(runTrackingLoop);
      }
      return;
    }
    
    const results = faceLandmarkerRef.current.detectForVideo(
      videoRef.current,
      performance.now()
    );
    
    if (results.faceLandmarks && results.faceLandmarks.length > 0) {
      const landmarks = results.faceLandmarks[0];
      
      // Get mouth landmarks
      const leftCorner = landmarks[MOUTH_LANDMARKS.LEFT_CORNER];
      const rightCorner = landmarks[MOUTH_LANDMARKS.RIGHT_CORNER];
      const upperLip = landmarks[MOUTH_LANDMARKS.UPPER_LIP];
      const lowerLip = landmarks[MOUTH_LANDMARKS.LOWER_LIP];
      
      if (leftCorner && rightCorner && upperLip && lowerLip) {
        const { calibration, gestureActions } = getGestureConfig();
        
        // Calculate mouth dimensions (normalized 0-1)
        const mouthWidth = Math.abs(rightCorner.x - leftCorner.x);
        const mouthHeight = Math.abs(lowerLip.y - upperLip.y);
        
        // Calculate ratios relative to neutral
        const openRatio = mouthHeight / calibration.neutralHeight;
        const smileRatio = mouthWidth / calibration.neutralWidth;
        
        const now = Date.now();
        const debounceMs = 500;
        const canTrigger = now - lastActionRef.current > debounceMs;
        
        if (canTrigger) {
          // Detect gestures and map to actions
          // Use same names as MouthGestureSetup: 'open_mouth', 'smile', 'kiss'
          let detectedGesture: string | null = null;
          
          if (openRatio > calibration.openThreshold) {
            detectedGesture = 'open_mouth';
          } else if (smileRatio > calibration.smileThreshold) {
            detectedGesture = 'smile';
          } else if (calibration.puckerThreshold && smileRatio < calibration.puckerThreshold) {
            detectedGesture = 'kiss';
          }
          
          if (detectedGesture) {
            const action = gestureActions[detectedGesture] as ScanningAction | undefined;
            
            if (action) {
              lastActionRef.current = now;
              // Use unified scanning system
              dispatchScanningAction(action);
            }
          }
        }
      }
    }
    
    if (isActiveRef.current) {
      animationFrameRef.current = requestAnimationFrame(runTrackingLoop);
    }
  }, [getGestureConfig]);
  
  // Start/stop tracking based on input method
  useEffect(() => {
    if (!shouldTrack) {
      cleanup();
      return;
    }
    
    isActiveRef.current = true;
    
    const startTracking = async () => {
      const mpReady = await initializeMediaPipe();
      if (!mpReady || !isActiveRef.current) return;
      
      const webcamReady = await setupWebcam();
      if (!webcamReady || !isActiveRef.current) return;
      
      setIsActive(true);
      runTrackingLoop();
    };
    
    startTracking();
    
    return cleanup;
  }, [shouldTrack, isSetupPage, cleanup, initializeMediaPipe, setupWebcam, runTrackingLoop]);
  
  // No visible UI
  return null;
}
